import { useNavigate } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import { X, CircleArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import { toast } from "react-hot-toast";
import ToolTipButton from './ToolTipButton';
import PropTypes from 'prop-types';


export function Button({
  children,
  onClick,
  type = "button",
  disabled = false,
  variant = "primary", // "primary" or "secondary"
  className = "",
  ...rest
}) {
  // Base shape and sizing for all buttons
  const base = "px-4 py-2 font-semibold rounded-md";

  // Primary = filled orange + white text; on hover only darken background
  const primaryClasses = `
  bg-[var(--color-primary)]
  text-[var(--bg-base)]
  border-none
  px-4 py-2
  rounded-md
  font-bold
  mr-4
  cursor-pointer
  transition-colors ease
  hover:bg-[var(--color-primary-hover)]
`;

  // Secondary = transparent background, orange text + border; on hover darken text/border, but keep background transparent
  const secondaryClasses = `
  bg-[rgba(25,118,210,0.1)]
  text-[var(--color-secondary)]
  border border-[var(--color-secondary)]
  px-4 py-2
  rounded-md
  font-bold
  cursor-pointer
  transition-colors ease 
  hover:bg-[rgba(25,118,210,0.1)]
  hover:text-[var(--color-secondary)]
`;

  const variantClasses =
    variant === "secondary" ? secondaryClasses : primaryClasses;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`
        ${base}
        ${variantClasses}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
      `}
      {...rest}
    >
      {children}
    </button>
  );
}



export function LinkButton({
  children,
  onClick,
  className = "",
  ...rest
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        bg-transparent
        border-none
        px-0 py-0
        text-sm
        text-[var(--color-primary)]
        hover:text-[var(--color-primary)]
        focus:outline-none
        ${className}
      `}
      {...rest}
    >
      {children}
    </button>
  );
}




export const FormInput = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  className = "",
  textarea = false,
  ...rest
}) => {
  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-[var(--text-primary)] mb-1"
        >
          {label} {required && <span className="text-[var(--color-error)]">*</span>}
        </label>
      )}

      {textarea ? (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className="
            w-full
            bg-[var(--bg-surface)]
            text-[var(--text-primary)]
            p-2
            rounded
            border
            border-[var(--border)]
            focus:outline-none
            focus:ring-2
            focus:ring-[var(--color-primary)]
          "
          {...rest}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          className="
            w-full
            bg-[var(--bg-surface)]
            text-[var(--text-primary)]
            p-2
            rounded
            border
            border-[var(--border)]
            focus:outline-none
            focus:ring-2
            focus:ring-[var(--color-primary)]
          "
          {...rest}
        />
      )}
    </div>
  );
};

export const FormSelect = ({
  label,
  name,
  value,
  onChange,
  required = false,
  className = "",
  options = [],
  ...rest
}) => {
  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-[var(--text-primary)] mb-1"
        >
          {label} {required && <span className="text-[var(--color-error)]">*</span>}
        </label>
      )}

      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="
          w-full
          bg-[var(--bg-surface)]
          text-[var(--text-primary)]
          p-2
          rounded
          border
          border-[var(--border)]
          focus:outline-none
          focus:ring-2
          focus:ring-[var(--color-primary)]
        "
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};


export const Modal = ({ title, onClose, children }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-3xl mx-4 overflow-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button onClick={onClose} className="text-gray-500 text-2xl font-bold">&times;</button>
        </div>
        <div className="mt-2">{children}</div>
      </div>
    </div>
  );
};


export const BackButton = ({ to, onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(); // prioritize custom logic
    } else if (to) {
      navigate(to);
    } else {
      navigate(-1); // fallback to history
    }
  };

  return (
    <ToolTipButton
      icon={CircleArrowLeft}
      tooltip="Back"
      iconSize={32}          // ← bump it up
      onClick={handleClick}
      className="lg:hidden absolute top-4 left-0 z-50 text-[var(--text-secondary)]"
    />
  );
};


export const MultiDigitInputWrong = ({ length = 7, onComplete, onIncomplete }) => {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputsRef = useRef([]);

  useEffect(() => {
    setDigits(Array(length).fill(''));
  }, [length]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    const fullCode = newDigits.join('');

    if (newDigits.includes('')) {
      onIncomplete?.(fullCode);
    } else if (fullCode.length === length) {
      onComplete?.(fullCode);  // Call onComplete only when input is complete
    }

    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 text-black font-semibold justify-center">
      {digits.map((digit, i) => (
        <input
          key={i}
          type="text"
          inputMode="numeric"
          value={digit}
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          ref={(el) => (inputsRef.current[i] = el)}
          className="w-10 h-12 text-center text-lg border rounded border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      ))}
    </div>
  );
};


export const MultiDigitInput = ({
  length = 7,
  value, // This will be `manualCode` from your parent
  onComplete,
  onIncomplete,
}) => {
  // Internal state for individual digit values
  const [digits, setDigits] = useState(Array(length).fill(''));
  // Refs for input elements to manage focus
  const inputsRef = useRef([]);
  // Ref to track the previous value of the `value` prop to detect when it's cleared
  const prevValuePropRef = useRef(value);

  // Effect 1: Handles changes in the `length` prop.
  // Resets the internal `digits` array structure if `length` changes.
  useEffect(() => {
    const newDigitsArray = Array(length).fill('');
    // If a `value` prop is currently provided, try to initialize based on it
    // respecting the new length.
    const currentPropValue = String(value === undefined || value === null ? '' : value);
    for (let i = 0; i < length; i++) {
      if (i < currentPropValue.length) {
        newDigitsArray[i] = currentPropValue[i];
      }
    }
    setDigits(newDigitsArray);

    // Optionally, if the component becomes effectively empty due to length change
    // and no overriding value, focus the first input.
    // However, primary focus-on-clear is handled by the effect below.
    // if (currentPropValue === '' && inputsRef.current[0]) {
    //   inputsRef.current[0].focus();
    // }
  }, [length]); // Only re-run if length changes. `value` is read for initialization.

  // Effect 2: Synchronizes with the `value` prop from the parent.
  // This effect makes the component "controlled" by the parent's `manualCode`.
  // It also handles the focus reset when `manualCode` is cleared.
  useEffect(() => {
    const currentPropValue = String(value === undefined || value === null ? '' : value);
    const newDigitsArrayBasedOnProp = Array(length).fill('');

    for (let i = 0; i < length; i++) {
      if (i < currentPropValue.length) {
        newDigitsArrayBasedOnProp[i] = currentPropValue[i];
      }
    }

    // Update internal `digits` state if it differs from what the `value` prop implies.
    // This ensures the component's display matches `manualCode`.
    if (newDigitsArrayBasedOnProp.join('') !== digits.join('')) {
      setDigits(newDigitsArrayBasedOnProp);
    }

    // FOCUS LOGIC:
    // If the `value` prop was changed by the parent to become empty (e.g., via setManualCode('')),
    // and it previously had a value, then focus the first input box.
    if (prevValuePropRef.current !== '' && currentPropValue === '' && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }

    // Update the ref to store the current `value` prop for the next comparison.
    prevValuePropRef.current = currentPropValue;

  }, [value, length, digits]); // Re-run when `value` or `length` props change, or internal `digits`
  // (digits in dep array helps sync if prop changes didn't immediately cause digit change)


  // Handles changes in individual input fields
  const handleChange = (inputValue, index) => {
    // Allow only single digits or an empty string (for clearing with backspace)
    if (!/^\d?$/.test(inputValue)) return;

    const newDigits = [...digits]; // Create a copy of the current internal digits
    newDigits[index] = inputValue;
    setDigits(newDigits); // Update internal state immediately for responsiveness

    const fullCode = newDigits.join('');

    // Notify the parent component through callbacks
    if (newDigits.includes('')) {
      onIncomplete?.(fullCode); // Pass the current (possibly incomplete) code
    } else if (fullCode.length === length) {
      onComplete?.(fullCode); // Pass the completed code
    }

    // Auto-focus next input if a digit is entered and it's not the last input
    if (inputValue && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  // Handles keydown events, specifically for Backspace behavior
  const handleKeyDown = (e, index) => {
    // If Backspace is pressed, the current input is empty, and it's not the first input
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Focus the previous input
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 text-black font-semibold justify-center">
      {/* Map over the internal `digits` state to render each input box */}
      {digits.map((digit, i) => (
        <input
          key={i}
          type="text" // Using "text" and inputMode="numeric" for broad compatibility
          inputMode="numeric" // Suggests a numeric keypad on mobile devices
          pattern="\d*" // Further hint for numeric input, though primarily for validation
          maxLength="1" // Ensure only one character can be entered per box
          value={digit} // The value of each input box is from our internal `digits` state
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          ref={(el) => (inputsRef.current[i] = el)} // Store ref to each input
          className="w-10 h-12 text-center text-lg border rounded border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={`Digit ${i + 1}`} // Accessibility improvement
        />
      ))}
    </div>
  );
};


export const Backsheet = ({ isOpen, onClose, title, children }) => {
  // Close on Esc key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // choose how to render title
  const renderTitle = () => {
    if (React.isValidElement(title)) {
      // if title is already JSX, render it directly
      return (
        <div id="backsheet-title" className="text-lg font-semibold">
          {title}
        </div>
      );
    } else {
      // otherwise assume it's a string
      return (
        <h3 id="backsheet-title" className="text-lg font-semibold">
          {title}
        </h3>
      );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300',
          { 'opacity-100 pointer-events-auto': isOpen, 'opacity-0 pointer-events-none': !isOpen }
        )}
        onClick={onClose}
      />

      {/* Backsheet Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="backsheet-title"
        className={clsx(
          'fixed bottom-0 left-0 right-0 w-full lg:4/5 mx-auto z-[110] bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-t-2xl shadow-lg transition-transform duration-300 max-h-[80vh] lg:max-h-[100vh] overflow-y-auto',
          {
            'translate-y-0': isOpen,
            'translate-y-full': !isOpen,
          }
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
          {renderTitle()}
          <button
            onClick={onClose}
            aria-label="Close"
            type="button"
            className="
              p-1 
              bg-transparent 
              border-none 
              focus:outline-none 
              hover:text-[var(--color-primary)]
              transition-colors
            "
          >
            <X className="w-5 h-5 text-[var(--text-primary)]" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </>
  );
};

Backsheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node
  ]).isRequired,
  children: PropTypes.node
};


/*
export function SlideToConfirmButton({ onConfirm, label = "Slide to confirm" }) {
  const containerRef = useRef(null);
  const knobRef = useRef(null);
  const [confirmed, setConfirmed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fillWidth, setFillWidth] = useState(0); // track green fill width

  const resetKnob = () => {
    const knob = knobRef.current;
    knob.style.transition = "transform 0.3s ease";
    knob.style.transform = `translateX(0px)`;
    setFillWidth(0);
    setTimeout(() => {
      knob.style.transition = "";
    }, 300);
  };

  const handleStart = (clientXStart, isTouch = false) => {
    if (confirmed || processing) return;

    const container = containerRef.current;
    const knob = knobRef.current;
    const containerRect = container.getBoundingClientRect();
    const knobWidth = knob.offsetWidth;

    const moveListener = (e) => {
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - clientXStart;
      const maxMove = containerRect.width - knobWidth;
      const newLeft = Math.min(Math.max(0, deltaX), maxMove);
      knob.style.transform = `translateX(${newLeft}px)`;
      setFillWidth(newLeft + knobWidth / 2); // adjust fill width slightly for nicer effect
    };

    const endListener = async (e) => {
      const clientXEnd = isTouch ? e.changedTouches[0].clientX : e.clientX;
      const deltaX = clientXEnd - clientXStart;
      const movedEnough = deltaX > containerRect.width - knobWidth - 10;

      if (movedEnough) {
        knob.style.transform = `translateX(${containerRect.width - knobWidth}px)`;
        setFillWidth(containerRect.width);
        setProcessing(true);

        try {
          await onConfirm?.();
          setConfirmed(true);
        } catch (error) {
          resetKnob();
        } finally {
          setProcessing(false);
        }
      } else {
        resetKnob();
      }

      setDragging(false);

      const removeEvents = () => {
        if (isTouch) {
          document.removeEventListener("touchmove", moveListener);
          document.removeEventListener("touchend", endListener);
        } else {
          document.removeEventListener("mousemove", moveListener);
          document.removeEventListener("mouseup", endListener);
        }
      };
      removeEvents();
    };

    if (isTouch) {
      document.addEventListener("touchmove", moveListener);
      document.addEventListener("touchend", endListener);
    } else {
      document.addEventListener("mousemove", moveListener);
      document.addEventListener("mouseup", endListener);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-12 bg-gray-200 rounded-md overflow-hidden select-none"
    >
      <div
        style={{ width: fillWidth }}
        className={`absolute top-0 left-0 h-full bg-green-500 rounded-md transition-[width] duration-50 ease-linear`}
      />

      <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm font-medium pointer-events-none">
        {confirmed ? "Confirmed" : label}
      </div>

      <div
        ref={knobRef}
        onTouchStart={(e) => {
          setDragging(true);
          handleStart(e.touches[0].clientX, true);
        }}
        onMouseDown={(e) => {
          setDragging(true);
          handleStart(e.clientX, false);
        }}
        className={`absolute top-0 left-0 h-full w-12 rounded-md z-10 transition-colors duration-300 ${confirmed ? "bg-green-700 cursor-default" : "bg-green-600 cursor-pointer"
          }`}
      />
    </div >
  );
}
*/



export function SlideToConfirmButton({
  onConfirm,
  label = "Slide to confirm",
  buttonLabel, // Optional text for desktop button
  confirmedLabel = "Confirmed", // Optional custom text after success
}) {
  const containerRef = useRef(null);
  const knobRef = useRef(null);
  const [confirmed, setConfirmed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fillWidth, setFillWidth] = useState(0);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  const resetKnob = () => {
    const knob = knobRef.current;
    if (!knob) return;
    knob.style.transition = "transform 0.3s ease";
    knob.style.transform = `translateX(0px)`;
    setFillWidth(0);
    setTimeout(() => {
      knob.style.transition = "";
    }, 300);
  };

  const handleStart = (clientXStart, isTouch = false) => {
    if (confirmed || processing) return;

    const container = containerRef.current;
    const knob = knobRef.current;
    const containerRect = container.getBoundingClientRect();
    const knobWidth = knob.offsetWidth;

    const moveListener = (e) => {
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - clientXStart;
      const maxMove = containerRect.width - knobWidth;
      const newLeft = Math.min(Math.max(0, deltaX), maxMove);
      knob.style.transform = `translateX(${newLeft}px)`;
      setFillWidth(newLeft + knobWidth / 2);
    };

    const endListener = async (e) => {
      const clientXEnd = isTouch ? e.changedTouches[0].clientX : e.clientX;
      const deltaX = clientXEnd - clientXStart;
      const movedEnough = deltaX > containerRect.width - knobWidth - 10;

      if (movedEnough) {
        knob.style.transform = `translateX(${containerRect.width - knobWidth}px)`;
        setFillWidth(containerRect.width);
        setProcessing(true);

        try {
          await onConfirm?.();
          setConfirmed(true);
        } catch (error) {
          resetKnob();
        } finally {
          setProcessing(false);
        }
      } else {
        resetKnob();
      }

      setDragging(false);
      const removeEvents = () => {
        if (isTouch) {
          document.removeEventListener("touchmove", moveListener);
          document.removeEventListener("touchend", endListener);
        } else {
          document.removeEventListener("mousemove", moveListener);
          document.removeEventListener("mouseup", endListener);
        }
      };
      removeEvents();
    };

    if (isTouch) {
      document.addEventListener("touchmove", moveListener);
      document.addEventListener("touchend", endListener);
    } else {
      document.addEventListener("mousemove", moveListener);
      document.addEventListener("mouseup", endListener);
    }
  };

  if (!isTouchDevice) {
    // Desktop: simple button
    return (
      <button
        onClick={async () => {
          setProcessing(true);
          try {
            await onConfirm?.();
            setConfirmed(true);
          } catch (err) {
            console.error(err);
          } finally {
            setProcessing(false);
          }
        }}
        disabled={confirmed || processing}
        className={`
          w-full h-12 rounded-md font-semibold flex items-center justify-center transition-colors
          ${confirmed
            ? "bg-[var(--color-primary-hover)] text-[var(--bg-base)] cursor-default"
            : "bg-[var(--color-primary)] text-[var(--bg-base)] hover:bg-[var(--color-primary-hover)]"
          }
          ${processing ? "opacity-70 cursor-not-allowed" : ""}
        `}
      >
        {processing ? (
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : confirmed ? (
          confirmedLabel
        ) : (
          buttonLabel || label
        )}
      </button>
    );
  }

  // Mobile: slider
  return (
    <div
      ref={containerRef}
      className="relative w-full h-12 bg-[var(--bg-surface)] rounded-md overflow-hidden select-none"
    >
      <div
        style={{ width: fillWidth }}
        className="absolute top-0 left-0 h-full bg-[var(--color-primary)] rounded-md transition-[width] duration-50 ease-linear"
      />

      <div className="absolute inset-0 flex items-center justify-center text-[var(--text-secondary)] text-sm font-medium pointer-events-none">
        {confirmed ? confirmedLabel : label}
      </div>

      <div
        ref={knobRef}
        onTouchStart={(e) => {
          setDragging(true);
          handleStart(e.touches[0].clientX, true);
        }}
        onMouseDown={(e) => {
          setDragging(true);
          handleStart(e.clientX, false);
        }}
        className={`
          absolute top-0 left-0 h-full w-12 rounded-md z-10 transition-colors duration-300
          ${confirmed
            ? "bg-[var(--color-primary-hover)] cursor-default"
            : "bg-[var(--color-primary)] cursor-pointer"
          }
        `}
      />
    </div>
  );
}


export function formatTimestamp(timestamp) {
  if (!timestamp || typeof timestamp !== 'object' || !timestamp.seconds) return '—';

  const date = new Date(timestamp.seconds * 1000);

  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function ToggleSwitch({
  checked,
  onChange,
  labelLeft = "Off",
  labelRight = "On",
  className = "",
}) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className={`text-sm font-medium transition ${!checked ? "text-blue-600" : "text-gray-400"}`}>
        {labelLeft}
      </span>

      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={() => onChange(!checked)}
        />
        <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-500 peer-checked:bg-blue-600 transition" />
        <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-md transform peer-checked:translate-x-5 transition" />
      </label>

      <span className={`text-sm font-medium transition ${checked ? "text-blue-600" : "text-gray-400"}`}>
        {labelRight}
      </span>
    </div>
  );
}

